import React, { Component } from 'react';
import { Alert, Button, Col, Form, Row } from 'reactstrap';
import { FormattedMessage } from 'react-intl';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';

import globalTranslations from '../../../i18n/globalTranslations';
import { createForm } from '../../../models/Form';
import { createFormField } from '../../../models/FormField';
import { minLength, mustMatch } from '../../../util/formValidators';
import ApiWrapper from '../../../services/ApiWrapper';
import UnexpectedError from '../../loader/UnexpectedError';
import ConfigStore from '../../../store/ConfigStore';
import { modelOf } from '../../../prop-types';
import FormGroupField from '../../form/FormGroupField';
import RequestState from '../../../types/RequestState';

const UpdateStatus = {
  NONE: 'NONE',
  REQUESTING: 'REQUESTING',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  SUCCESS: 'SUCCESS',
  PASSWORD_NOT_VALID: 'PASSWORD_NOT_VALID',
  PASSWORDS_DO_NOT_MATCH: 'PASSWORDS_DO_NOT_MATCH',
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR'
};

@observer
export class MyAccountChangePasswordForm extends Component {
  form = null;
  state = {
    status: UpdateStatus.NONE,
    requestState: RequestState.NONE
  };

  constructor(props) {
    super(props);

    const { configStore } = this.props;

    const fields = {
      currentPassword: createFormField({}, [
        minLength(
          <FormattedMessage {...globalTranslations.currentPasswordTitle} />,
          configStore.formValidation.password.minLength
        )
      ]),
      newPassword: createFormField({}, [
        minLength(
          <FormattedMessage {...globalTranslations.newPasswordTitle} />,
          configStore.formValidation.password.minLength
        )
      ]),
      newPassword_confirmation: createFormField({}, [
        mustMatch(() => this.form.fields.get('newPassword').value)
      ])
    };

    this.form = createForm(fields);
  }

  handleSubmit = (e) => {
    const { apiWrapper } = this.props;

    e.preventDefault();
    this.form.validate();
    if (this.form.valid) {
      this.setState({
        status: UpdateStatus.NONE,
        requestState: RequestState.LOADING
      });
      apiWrapper
        .apiAxios()
        .put('my-account/change-password', this.form.getFieldValues())
        .then(() => {
          this.setState({
            status: UpdateStatus.SUCCESS,
            requestState: RequestState.LOADED
          });
        })
        .catch((e) => {
          const status = e.response ? e.response.status : 500;
          const errorMap = {
            400: UpdateStatus.VALIDATION_ERROR,
            422: UpdateStatus.PASSWORD_NOT_VALID,
            500: UpdateStatus.UNEXPECTED_ERROR
          };
          this.setState({
            status: errorMap[status],
            requestState: RequestState.ERROR
          });
        });
    }
  };

  getField = (name, message, props) => {
    return (
      <div
        className={`MyAccountChangePasswordForm__field MyAccountInfoForm__field--${name}`}
      >
        <FormGroupField
          field={this.form.fields.get(name)}
          label={<FormattedMessage {...message} />}
          formName="MyAccountChangePasswordForm"
          fieldName={name}
          {...props}
        />
      </div>
    );
  };

  getForm = () => {
    return (
      <Form noValidate onSubmit={this.handleSubmit}>
        <Row>
          <Col md="6">
            <div className="MyAccountChangePasswordForm__row">
              {this.getField(
                'currentPassword',
                globalTranslations.currentPasswordTitle,
                {
                  type: 'password'
                }
              )}
              {this.getField(
                'newPassword',
                globalTranslations.newPasswordTitle,
                {
                  type: 'password',
                  autoComplete: 'new-password'
                }
              )}
              {this.getField(
                'newPassword_confirmation',
                globalTranslations.confirmPasswordTitle,
                {
                  type: 'password',
                  autoComplete: 'new-password'
                }
              )}
              <Button
                className="MyAccountChangePasswordForm__submit"
                color="primary"
                disabled={this.form.valid === false}
              >
                {<FormattedMessage {...globalTranslations.update} />}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>
    );
  };

  renderStatus = (status) => {
    let message = null;

    switch (status) {
      case UpdateStatus.NONE:
      case UpdateStatus.REQUESTING:
        break;
      case UpdateStatus.SUCCESS:
        message = (
          <Alert color="success">
            <FormattedMessage
              id="account.password.SUCCESS"
              defaultMessage="Your password was updated successfully."
            />
          </Alert>
        );
        break;
      case UpdateStatus.VALIDATION_ERROR:
        // This is just in case, as it should actually never happen, because we validate the fields client side.
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="account.password.VALIDATION_ERROR"
              defaultMessage="Information you provided is not valid. Please recheck the form."
            />
          </Alert>
        );
        break;
      case UpdateStatus.PASSWORD_NOT_VALID:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="account.password.PASSWORD_NOT_VALID"
              defaultMessage="Your password does not match the password in our database. Please try again."
            />
          </Alert>
        );
        break;
      case UpdateStatus.PASSWORDS_DO_NOT_MATCH:
        message = (
          <Alert color="danger">
            <FormattedMessage
              id="account.password.PASSWORDS_DO_NOT_MATCH"
              defaultMessage="The password confirmation text must match your chosen password."
            />
          </Alert>
        );
        break;
      default:
      case UpdateStatus.UNEXPECTED_ERROR:
        message = <UnexpectedError />;
    }
    return message;
  };

  render() {
    const { status } = this.state;
    return (
      <div className="MyAccountChangePasswordForm">
        {this.renderStatus(status)}
        {status !== UpdateStatus.SUCCESS && this.getForm()}
      </div>
    );
  }
}

MyAccountChangePasswordForm.propTypes = {
  configStore: modelOf(ConfigStore).isRequired,
  apiWrapper: PropTypes.instanceOf(ApiWrapper).isRequired
};

export default inject('apiWrapper', 'configStore')(MyAccountChangePasswordForm);



// WEBPACK FOOTER //
// ./src/components/my-account/MyAccountChangePasswordForm/index.jsx